import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProgressSteps } from '@/components/ui/ProgressSteps';

export interface WizardContainerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  iconBgColor?: string;
  headerGradient?: string;
  totalSteps: number;
  currentStep: number;
  children: ReactNode;
  onBack?: () => void;
  showBack?: boolean;
}

export function WizardContainer({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  iconBgColor = 'bg-orange-500/20',
  headerGradient = 'from-orange-600 to-orange-700',
  totalSteps,
  currentStep,
  children,
  onBack,
  showBack = false,
}: WizardContainerProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTab);
      firstElement?.focus();

      return () => {
        document.removeEventListener('keydown', handleTab);
      };
    }
  }, [isOpen]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-title"
    >
      <div
        ref={modalRef}
        className={cn(
          'w-full max-w-md bg-slate-800 rounded-xl shadow-xl border border-slate-700 overflow-hidden',
          'animate-in fade-in-0 zoom-in-95 duration-200'
        )}
      >
        {/* Header with gradient */}
        <div className={cn('relative px-6 py-4 bg-gradient-to-r', headerGradient)}>
          <div className="flex items-center gap-4">
            {showBack && onBack && currentStep > 0 && (
              <button
                onClick={onBack}
                className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Zurück"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            {icon && (
              <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', iconBgColor)}>
                {icon}
              </div>
            )}
            <div className="flex-1">
              <h3 id="wizard-title" className="text-lg font-semibold text-white">
                {title}
              </h3>
              {subtitle && <p className="text-sm text-white/70">{subtitle}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress indicator */}
        {totalSteps > 1 && (
          <div className="px-6 py-3 bg-slate-800/50 border-b border-slate-700/50">
            <ProgressSteps total={totalSteps} current={currentStep} />
          </div>
        )}

        {/* Body */}
        <div className="p-6">{children}</div>
      </div>
    </div>,
    document.body
  );
}
